<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from 'radix-vue'

const router = useRouter()
const authStore = useAuthStore()

const initials = computed(() => {
  const p = authStore.userProfile
  if (!p) return null
  const first = p.user_first_name?.trim()?.[0] ?? ''
  const last = p.user_last_name?.trim()?.[0] ?? ''
  return (first + last).toUpperCase() || null
})

const displayName = computed(() => {
  const p = authStore.userProfile
  if (!p) return ''
  return [p.user_first_name, p.user_last_name].filter(Boolean).join(' ')
})

const email = computed(() => authStore.currentUser?.email ?? '')

async function signOut() {
  await authStore.logout()
  router.push('/login')
}
</script>

<template>
  <DropdownMenuRoot>
    <DropdownMenuTrigger
      class="w-8 h-8 bg-primary-container text-white font-mono text-xs font-bold flex items-center justify-center rounded hover:brightness-110 focus:outline-none focus:ring-1 focus:ring-primary/30 cursor-pointer"
      :aria-label="displayName || 'User menu'"
    >
      <span v-if="initials">{{ initials }}</span>
      <span v-else class="material-symbols-outlined text-[16px]">person</span>
    </DropdownMenuTrigger>

    <DropdownMenuPortal>
      <DropdownMenuContent
        class="z-50 bg-surface-container-high border border-border rounded min-w-48 p-1 shadow-xl"
        :side-offset="8"
        align="end"
      >
        <div class="px-3 py-2 mb-1">
          <p class="font-mono text-xs font-bold text-on-surface uppercase tracking-wide">{{ displayName }}</p>
          <p class="font-mono text-[0.65rem] text-outline mt-0.5">{{ email }}</p>
        </div>

        <DropdownMenuSeparator class="h-px bg-surface-container my-1" />

        <DropdownMenuItem
          class="flex items-center gap-2 px-3 py-2 font-mono text-xs text-on-surface cursor-pointer focus:outline-none data-[highlighted]:bg-surface-container-highest"
          @select="router.push('/settings')"
        >
          <span class="material-symbols-outlined text-[16px] text-outline">settings</span>
          Settings
        </DropdownMenuItem>

        <DropdownMenuItem
          v-if="authStore.userProfile?.role === 'admin'"
          class="flex items-center gap-2 px-3 py-2 font-mono text-xs text-on-surface cursor-pointer focus:outline-none data-[highlighted]:bg-surface-container-highest"
          @select="router.push('/agency')"
        >
          <span class="material-symbols-outlined text-[16px] text-outline">business</span>
          Agency Settings
        </DropdownMenuItem>

        <DropdownMenuItem
          v-if="authStore.isSuperAdmin"
          class="flex items-center gap-2 px-3 py-2 font-mono text-xs text-on-surface cursor-pointer focus:outline-none data-[highlighted]:bg-surface-container-highest"
          @select="router.push('/super-admin')"
        >
          <span class="material-symbols-outlined text-[16px] text-outline">shield</span>
          Platform Admin
        </DropdownMenuItem>

        <DropdownMenuSeparator class="h-px bg-surface-container my-1" />

        <DropdownMenuItem
          class="flex items-center gap-2 px-3 py-2 font-mono text-xs text-error cursor-pointer focus:outline-none data-[highlighted]:bg-surface-container-highest"
          @select="signOut"
        >
          <span class="material-symbols-outlined text-[16px]">logout</span>
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>
